import { useQuery } from "@tanstack/react-query";

import { request, switchedOff } from "../../lib/http";
import { FeatureOff } from "../FeatureOff";
import { Empty, ErrorState, Loading } from "../States";
import { TableScroll } from "../TableScroll";

/** One entry of `GET /modules/catalog`. Untyped in the contract (a plain dict), so spelled out. */
export interface CatalogModule {
  key: string;
  name: string;
  shell_area: number;
  status: "available" | "preview" | "blocked" | string;
  enabled: boolean;
  reason: string;
  summary: string;
  needs: string[];
  blocked_on: string | null;
  evidence: string;
  request_types: string[];
}

export interface Catalog {
  modules: CatalogModule[];
  enabled: number;
  blocked_on: string[];
}

const STATUS_TONE: Record<string, string> = { available: "ok", preview: "review", blocked: "fail" };

export function fetchCatalog(): Promise<Catalog> {
  return request<Catalog>("/modules/catalog");
}

export function ModuleFlags({ catalog }: { catalog: Catalog }) {
  if (catalog.modules.length === 0) return <Empty title="No modules in the catalog" />;
  return (
    <div className="stack">
      <p className="muted">
        <strong className="num">{catalog.enabled} of {catalog.modules.length} modules on</strong> in this environment.
        Switches are deployment settings (MODULES_ENABLED and MODULES_DISABLED), not changed from this screen.
      </p>
      <div className="card table-scroll">
        <TableScroll label="Module switches">
        <table>
          <thead>
            <tr><th>Module</th><th>Status</th><th>Here</th><th>Why</th></tr>
          </thead>
          <tbody>
            {catalog.modules.map((m) => (
              <tr key={m.key}>
                <td>
                  <div>{m.name}</div>
                  <div className="muted mono-sm">{m.key}</div>
                </td>
                <td><span className={`pill ${STATUS_TONE[m.status] ?? "info"}`}>{m.status}</span></td>
                <td><span className={`pill ${m.enabled ? "ok" : "info"}`}>{m.enabled ? "On" : "Off"}</span></td>
                <td className="muted">
                  {m.blocked_on ? <>Waiting on: {m.blocked_on}</> : m.reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroll>
      </div>
    </div>
  );
}

export function ModulesTab() {
  const query = useQuery({ queryKey: ["admin", "catalog"], queryFn: fetchCatalog });
  const off = switchedOff(query.error, [404]);
  if (query.isPending) return <Loading what="Reading the module catalog" />;
  if (off !== null) return <FeatureOff feature="The module catalog" reason={off} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  return <ModuleFlags catalog={query.data} />;
}
